import type { AuthUser } from "./auth-context";

/**
 * Every permission code the backend seeds (atlas-oms-backend/migrations).
 * Mirrors the route guards one-to-one — see the spec §4. Adding a backend
 * permission means adding it here; a typo at a call site is a compile error.
 */
export const ALL_PERMISSIONS = [
  "users:read",
  "users:manage",
  "stock:read",
  "stock:write",
  "stock:audit:read_all",
  "sku:create",
  "sku:delete",
  "inventory_ratio:write",
  "inventory_ratio:delete",
  "listing:write",
  "orders:read",
  "orders:write",
  "orders:cancel",
  "gwp:read",
  "gwp:write",
  "reports:read",
  "promotions:read",
  "promotions:manage",
  "notifications:read",
  "notifications:write",
  "notifications:settings",
  "notifications:deliveries",
  "integrations:read",
  "integrations:write",
] as const;

export type Permission = (typeof ALL_PERMISSIONS)[number];

export const ADMIN_ROLE = "admin";

/**
 * True when the user's permission list contains `p`. No admin special-casing:
 * the admin role holds every permission row in the database (cross-join seed),
 * so a plain lookup is already correct for admins.
 */
export function hasPermission(user: AuthUser | null | undefined, p: Permission): boolean {
  return user?.permissions.includes(p) ?? false;
}

/** True only for routes the backend guards with RequireRole("admin"). */
export function hasAdminRole(user: AuthUser | null | undefined): boolean {
  return user?.roles.includes(ADMIN_ROLE) ?? false;
}
